import { getAuthToken, removeAuthToken, setAuthToken } from '@/lib/api/authToken'
import { fetchShowFeature } from '@/lib/api/featureToggleApi'
import { bindJPushRegistrationID, unbindJPushRegistrationID } from '@/lib/api/jpushApi'
import {
  login,
  loginViaThirdParty,
  LoginViaThirdPartyArgs,
  passwordFreeLogin,
  registerOrBindViaThirdParty,
  RegisterOrBindViaThirdPartyArgs,
} from '@/lib/api/sessionApi'
import { logOutRevenueCat } from '@/lib/revenuecat'
import { getJPushRegistrationID } from '@/lib/jpush'
import { V2User } from '@/typings/v2Api'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Platform } from 'react-native'

type SessionState = {
  initialized: boolean
  signedIn: boolean

  appReviewMode: boolean
  /** The Jiri entry is shown only to billing accounts with this platform's toggle (`JIRI_TOGGLE`). */
  jiriMobile: boolean
}

// Mobile web rolls out on its own toggle, so it can open to accounts apart from the App.
const JIRI_TOGGLE = Platform.OS === 'web' ? 'jiri_mobile_h5' : 'jiri_mobile'

const initialState: SessionState = {
  initialized: false,
  signedIn: false,

  appReviewMode: false,
  jiriMobile: false,
}

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    initSession: state => {
      state.initialized = true
      state.signedIn = !!getAuthToken()
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.signedIn = true
        state.appReviewMode = !!action.payload[0].toggles?.app_review_mode
        setAuthToken(action.payload[0].token)
      })
      .addCase(passwordFreeLoginThunk.fulfilled, (state, action) => {
        state.signedIn = true
        state.appReviewMode = !!action.payload[0].toggles?.app_review_mode
        setAuthToken(action.payload[0].token)
      })
      .addCase(loginViaThirdPartyThunk.fulfilled, (state, action) => {
        state.signedIn = true
        state.appReviewMode = !!action.payload[0].toggles?.app_review_mode
        setAuthToken(action.payload[0].token)
      })
      .addCase(registerOrBindViaThirdPartyThunk.fulfilled, (state, action) => {
        state.signedIn = true
        state.appReviewMode = !!action.payload[0].toggles?.app_review_mode
        setAuthToken(action.payload[0].token)
      })
      .addCase(logoutThunk.fulfilled, (state, action) => {
        state.signedIn = false
        state.appReviewMode = false
        state.jiriMobile = false
      })
      .addCase(switchUserThunk.fulfilled, state => {
        // The Jiri entry is the billing account's; a read for the new one that fails must not leave the old one's.
        state.jiriMobile = false
      })
      .addCase(fetchTogglesThunk.fulfilled, (state, action) => {
        if (action.payload.appReviewMode !== undefined) state.appReviewMode = action.payload.appReviewMode
        if (action.payload.jiriMobile !== undefined) state.jiriMobile = action.payload.jiriMobile
      })
  },
})

export type LoginResult = [V2User, ...V2User[]]

export const loginThunk = createAsyncThunk<LoginResult, { identity: string; password: string }>(
  'session/login',
  async ({ identity, password }, thunkAPI) => {
    const result = await login(identity, password)
    if (!result.success) {
      return thunkAPI.rejectWithValue({ message: result.errors.join(', ') })
    }
    return result.data.length ? (result.data as LoginResult) : thunkAPI.rejectWithValue({ message: 'Not signed in' })
  }
)

export const loginViaThirdPartyThunk = createAsyncThunk<
  LoginResult,
  LoginViaThirdPartyArgs,
  { rejectValue: { message: string; usersNotExist?: boolean } }
>('session/loginViaThirdParty', async (args, thunkAPI) => {
  const result = await loginViaThirdParty(args)
  if (!result.success) {
    return thunkAPI.rejectWithValue({ message: 'Not signed in' })
  }
  return result.data.length
    ? (result.data as LoginResult)
    : thunkAPI.rejectWithValue({ message: 'Not signed in', usersNotExist: true })
})

export const registerOrBindViaThirdPartyThunk = createAsyncThunk<LoginResult, RegisterOrBindViaThirdPartyArgs>(
  'session/registerOrBindViaThirdParty',
  async (args, thunkAPI) => {
    const result = await registerOrBindViaThirdParty(args)
    if (!result.success) {
      return thunkAPI.rejectWithValue({ message: 'Not signed in' })
    }
    return result.data.length ? (result.data as LoginResult) : thunkAPI.rejectWithValue({ message: 'Not signed in' })
  }
)

export const passwordFreeLoginThunk = createAsyncThunk<LoginResult, { mobile: string; code: string }>(
  'session/passwordFreeLogin',
  async ({ mobile, code }, thunkAPI) => {
    const result = await passwordFreeLogin(mobile, code)
    if (!result.success) {
      return thunkAPI.rejectWithValue({ message: result.errors.join(', ') })
    }
    return result.data.length ? (result.data as LoginResult) : thunkAPI.rejectWithValue({ message: 'Not signed in' })
  }
)

/** A toggle that could not be read is left as it was. */
export const fetchTogglesThunk = createAsyncThunk<{ appReviewMode?: boolean; jiriMobile?: boolean }>(
  'session/fetchToggles',
  async (_, thunkAPI) => {
    const [appReviewMode, jiriMobile] = await Promise.allSettled([
      fetchShowFeature('app_review_mode'),
      fetchShowFeature(JIRI_TOGGLE, { forBillingAccountOwner: true }),
    ])
    if (appReviewMode.status === 'rejected' && jiriMobile.status === 'rejected') {
      return thunkAPI.rejectWithValue({ message: 'Failed to fetch toggles' })
    }
    return {
      appReviewMode: appReviewMode.status === 'fulfilled' ? !!appReviewMode.value.data.showFeature : undefined,
      jiriMobile: jiriMobile.status === 'fulfilled' ? !!jiriMobile.value.data.showFeature : undefined,
    }
  }
)

export const logoutThunk = createAsyncThunk<void>('session/logout', async (_) => {
  const prevUserToken = getAuthToken()
  removeAuthToken()

  // Non-blocking cleanup
  tryToUnbindJPushRegistrationID(prevUserToken)
  logOutRevenueCat()
})

export const switchUserThunk = createAsyncThunk<void, { userToken: string }>(
  'session/switchUser',
  async ({ userToken }) => {
    const prevUserToken = getAuthToken()
    setAuthToken(userToken)

    // Non-blocking cleanup & setup
    tryToUnbindJPushRegistrationID(prevUserToken)
    logOutRevenueCat()
    tryToBindJPushRegistrationID(userToken)
  }
)

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Timeout')), ms)),
  ])
}

async function tryToUnbindJPushRegistrationID(userToken?: string | null) {
  try {
    await withTimeout(async function () {
      const registrationID = await getJPushRegistrationID()
      if (registrationID && userToken) {
        await unbindJPushRegistrationID(registrationID, userToken)
      }
    }(), 5000)
  } catch (error) {
    console.error('[Session] Failed to unbind JPush registration ID:', error)
  }
}

async function tryToBindJPushRegistrationID(userToken?: string | null) {
  try {
    await withTimeout(async function () {
      const registrationID = await getJPushRegistrationID()
      if (registrationID && userToken) {
        await bindJPushRegistrationID(registrationID, userToken)
      }
    }(), 5000)
  } catch (error) {
    console.error('[Session] Failed to bind JPush registration ID:', error)
  }
}

export const { initSession } = sessionSlice.actions

export default sessionSlice.reducer
